import { useTheme } from '@emotion/react';
import {
  Body,
  Box,
  Button,
  Center,
  Container,
  HStack,
  Heading,
  VStack,
} from '@hover/blueprint';
import { iconUpload } from '@hover/icons';
import blackHoverLogo from '@hover/tokens-brand/assets/logo-full--black.svg?url';
import React, { useEffect, useRef, useState } from 'react';

import type { BottomTextProps } from 'src/modules/homeownerApp/components/Projects/Menu/AddSavedToProject/BottomText';
import {
  BeforeAfterSwitch,
  StyleSelector,
} from 'src/modules/pro/components/ImageGrid/ImageGrid';
import type { LandingPageStyle } from 'src/modules/pro/components/ImageGrid/SampleImage';
import {
  SampleImage,
  preloadSampleImages,
} from 'src/modules/pro/components/ImageGrid/SampleImage';
import { Image } from 'src/shared/components';
import { hideScrollbarsStyles } from 'src/shared/helpers/styles';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useSetOnetrustCookiesManually } from 'src/shared/hooks/useSetOnetrustCookiesManually';

import { useHomeownerAppContext } from '../../hooks';
import { useShowSignInSheet } from '../../hooks/useShowSignInSheet';
import { HomeownerAppMenuNames } from '../../providers/HomeownerAppProvider';
import { BasicCard } from '../Card';

const EmbeddedDesignerLandingPage = () => {
  useShowSignInSheet();
  useSetOnetrustCookiesManually();
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

  const [style, setStyle] = useState<LandingPageStyle | undefined>(undefined);
  const [isBeforeSelected, setIsBeforeSelected] = useState(false);

  const carouselRef = useRef<HTMLDivElement>(null);

  // Sets initial position of card container for mobile so that
  // a card or two cards are centered in the window
  // We might be able to share more logic between this and the InfinityCarousel
  // but I was running out of time and I don't want the animation features
  useEffect(() => {
    if (carouselRef.current && window.innerWidth) {
      const cardOffset = window.innerWidth * 0.5;
      carouselRef.current.scrollLeft = cardOffset;
    }
  }, []);

  const imageHeight = { base: 'calc(100vh - 330px)' };
  const hideSmall = { base: 'none', tablet: 'block' };
  const showSmall = { base: 'block', tablet: 'none' };
  const carouselItemProps = {
    scrollSnapAlign: 'center',
    px: 100,
    flexShrink: 0,
    width: 'calc((100vh - 330px) * 3 / 4 + 8px)',
    sx: { scrollSnapStop: 'always' },
  };

  const parentContainerSpacing = {
    mx: { base: 400, tablet: 800 },
    p: 0,
    width: 'auto',
  };

  const bottomTextProps: BottomTextProps = {
    display: { base: 'none', tablet: 'block' },
    primaryProps: { size: 'small' },
    secondaryProps: { size: 'small', marginTop: '0px !important' },
  };

  const { setCurrentMenu } = useHomeownerAppContext();

  const onAddPhotoClick = () => {
    setCurrentMenu(HomeownerAppMenuNames.UPLOAD_PHOTO);
  };

  useEffect(preloadSampleImages, []);

  const TryItNowCard = () => {
    const { colors } = useTheme();

    return (
      <Box marginTop="0px !important" width="100%">
        <BasicCard
          backgroundColorProp={colors.neutral[0]}
          boxSizing="initial"
          display={hideSmall}
          height="auto"
          marginLeft={{ base: 100, tablet: 500 }}
          mt={400}
          padding={{ base: 400, desktop: 500 }}
        >
          <HStack margin="0px">
            <VStack alignItems="flex-start">
              <Heading display={hideSmall} size={200}>
                {t('embed.ctaHeader')}
              </Heading>
              <Body
                marginTop="0px !important"
                size={300}
                textColor="neutral.600"
              >
                {t('embed.ctaDescription')}
              </Body>
            </VStack>
            <Button
              iconBefore={iconUpload}
              onClick={onAddPhotoClick}
              size="large"
            >
              {t('embed.ctaButtonText')}
            </Button>
          </HStack>
        </BasicCard>
      </Box>
    );
  };

  return (
    <>
      <Container background="neutral.50" height="100%" p={0}>
        <Container
          alignItems="center"
          margin="0 auto"
          px={0}
          py={{ base: 600, tablet: 700 }}
        >
          <Container {...parentContainerSpacing} textAlign="center">
            <Heading
              margin="0px"
              mb="0px !important"
              size={{ base: 'medium', tablet: 'xl' }}
            >
              {t('embed.header')}
            </Heading>
            <Body
              marginBottom={0}
              marginTop="0px !important"
              pt={{ base: '200', tablet: '300' }}
              size={300}
              textColor="neutral.600"
            >
              {t('embed.subHeader')}
            </Body>
          </Container>
          <StyleSelector
            mb={{ base: '400', tablet: '500' }}
            mt={{ base: '400', tablet: '400' }}
            overflow="hidden"
            pb="0"
            selectedStyle={style}
            setIsBeforeSelected={setIsBeforeSelected}
            setStyle={setStyle}
          />
          {/* Carousel of sample images on small screens */}
          <Box
            display={showSmall}
            justifyContent="center"
            position="relative"
            width="100%"
          >
            <Box
              overflow="auto"
              paddingX={{ base: '500' }}
              ref={carouselRef}
              scrollSnapType="x mandatory"
              sx={hideScrollbarsStyles()}
            >
              <Box {...carouselItemProps}>
                <SampleImage
                  bottomTextProps={bottomTextProps}
                  height={imageHeight}
                  isBeforeSelected={isBeforeSelected}
                  ratio={3 / 4}
                  spaceType="Bedroom"
                  styleName={style || 'Farmhouse'}
                />
              </Box>
              <Box {...carouselItemProps}>
                <SampleImage
                  bottomTextProps={bottomTextProps}
                  height={imageHeight}
                  isBeforeSelected={isBeforeSelected}
                  ratio={3 / 4}
                  spaceType="Exterior"
                  styleName={style || 'Farmhouse'}
                />
              </Box>
              <Box {...carouselItemProps}>
                <SampleImage
                  bottomTextProps={bottomTextProps}
                  height={imageHeight}
                  isBeforeSelected={isBeforeSelected}
                  ratio={3 / 4}
                  spaceType="Kitchen"
                  styleName={style || 'Farmhouse'}
                />
              </Box>
              <Box {...carouselItemProps}>
                <SampleImage
                  bottomTextProps={bottomTextProps}
                  height={imageHeight}
                  isBeforeSelected={isBeforeSelected}
                  ratio={3 / 4}
                  spaceType="Living Room"
                  styleName={style || 'Farmhouse'}
                />
              </Box>
            </Box>
            <Box
              bottom={{ base: 300, tablet: 1000 }}
              justifyContent="center"
              left="0"
              position="absolute"
              right="0"
              zIndex="100"
            >
              <BeforeAfterSwitch
                isBeforeSelected={isBeforeSelected}
                setIsBeforeSelected={setIsBeforeSelected}
              />
            </Box>
          </Box>
          {/* Sample image on large screens */}
          <Box
            display={{ base: 'none', tablet: 'flex' }}
            paddingX={{ base: 500, tablet: 800 }}
          >
            <Box
              height="calc(100vh - 300px)"
              justifyContent="center"
              position="relative"
              width={{ base: '100%' }}
            >
              <Box width="100%">
                <SampleImage
                  bottomTextProps={bottomTextProps}
                  display="block"
                  height={imageHeight}
                  isBeforeSelected={isBeforeSelected}
                  spaceType="Exterior"
                  styleName={style || 'Farmhouse'}
                />
              </Box>
              <Box
                bottom={{ base: 1000, tablet: 800, desktop: 900 }}
                justifyContent="center"
                left="0"
                position="absolute"
                right="0"
                zIndex="100"
              >
                <BeforeAfterSwitch
                  isBeforeSelected={isBeforeSelected}
                  setIsBeforeSelected={setIsBeforeSelected}
                />
              </Box>
            </Box>
            <VStack width="100%">
              <Box height="calc(100vh - 460px)" width="100%">
                <SampleImage
                  bottomTextProps={bottomTextProps}
                  isBeforeSelected={isBeforeSelected}
                  marginLeft={{ base: 100, tablet: 500 }}
                  spaceType="Kitchen"
                  styleName={style || 'Contemporary'}
                />
                <SampleImage
                  bottomTextProps={bottomTextProps}
                  isBeforeSelected={isBeforeSelected}
                  marginLeft={{ base: 100, tablet: 500 }}
                  spaceType="Bedroom"
                  styleName={style || 'Mid-Century Modern'}
                />
              </Box>
              <TryItNowCard />
            </VStack>
          </Box>
          <Container {...parentContainerSpacing}>
            <Button
              boxSizing="border-box"
              display={showSmall}
              iconBefore={iconUpload}
              marginTop={400}
              onClick={onAddPhotoClick}
              size="large"
              width="100%"
            >
              {t('embed.ctaButtonTextSm')}
            </Button>
          </Container>
        </Container>
        <Container
          bottom={{ base: '16px', tablet: '24px' }}
          position="absolute"
        >
          <Center>
            <Body color="neutral.600" m={0} size="small">
              {t('embed.poweredBy')}
            </Body>
            <Image
              alt={t('navbar.logoAlt')}
              height="20px"
              marginLeft="300"
              marginTop="100"
              src={blackHoverLogo}
            />
          </Center>
        </Container>
      </Container>
    </>
  );
};

export { EmbeddedDesignerLandingPage };
